import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  CircularProgress
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import { authenticatedFetch } from '../../../utils/apiUtils';
import { getResearchTemplates, applyResearchTemplate } from '../../../utils/researchTemplatesUtils';

export default function Step1_UploadList({
  wizardState,
  onUpdateState,
  onNext,
  loading,
  setLoading
}) {
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(!!wizardState.fileId);
  const [dragActive, setDragActive] = useState(false);

  // Template state
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [applyingTemplate, setApplyingTemplate] = useState(false);

  // Fetch templates on mount
  useEffect(() => {
    const fetchTemplates = async () => {
      setLoadingTemplates(true);
      try {
        const data = await getResearchTemplates();
        setTemplates(data || []);
      } catch (err) {
        console.error('Error fetching templates:', err);
      }
      setLoadingTemplates(false);
    };
    fetchTemplates();
  }, []);

  // Handle apply template
  const handleApplyTemplate = async () => {
    if (!selectedTemplateId) {
      setError('Please select a template.');
      return;
    }
    if (!wizardState.fileId) {
      setError('Please upload a file first before applying a template.');
      return;
    }

    setApplyingTemplate(true);
    setError(null);

    try {
      const result = await applyResearchTemplate(selectedTemplateId);
      const config = result.config;

      // Check if template's column exists in uploaded file
      const templateColumn = config.selectedColumn;
      const fileColumns = wizardState.columns || [];

      if (templateColumn && !fileColumns.includes(templateColumn)) {
        setError(`Column "${templateColumn}" not found in this file. The template expects this column to exist.`);
        setApplyingTemplate(false);
        return;
      }

      // Pre-fill wizard state with template data
      onUpdateState({
        selectedColumn: templateColumn || null,
        researchInstructions: config.instructions || '',
        enableQualification: config.enableQualification !== false,
        enableCustomFields: config.enableCustomFields || false,
        customFields: config.customFields || [],
        enableWebSearch: config.enableWebSearch || false,
        webSearchInstructions: config.webSearchInstructions || '',
        appliedTemplateId: selectedTemplateId,
        appliedTemplateName: config.name
      });

      console.log('[TEMPLATE] Applied template:', config.name);

      // Skip Step 2, go directly to Step 3
      onNext(); // Go to Step 2
      onNext(); // Go to Step 3

    } catch (err) {
      console.error('Error applying template:', err);
      setError('Failed to apply template. Please try again.');
    }
    setApplyingTemplate(false);
  };

  // Handle file selection with auto-upload
  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      validateAndUpload(selectedFile);
    }
  };

  // Validate file type
  const isValidFileType = (file) => {
    const validTypes = ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
    const validExtensions = ['.csv', '.xlsx', '.xls'];

    return validTypes.includes(file.type) || validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
  };

  // Validate and upload file
  const validateAndUpload = async (selectedFile) => {
    setError(null);
    setUploadSuccess(false);

    if (!isValidFileType(selectedFile)) {
      setError('Please upload a CSV or Excel file (.csv, .xlsx, .xls)');
      return;
    }

    setFile(selectedFile);
    await autoUpload(selectedFile);
  };

  // Auto-upload file
  const autoUpload = async (fileToUpload) => {
    if (!fileToUpload) return;

    const formData = new FormData();
    formData.append('file', fileToUpload);

    try {
      setError(null);
      setLoading(true);

      const response = await authenticatedFetch('/api/research/upload', {
        method: 'POST',
        body: formData,
        headers: {}
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Upload failed with status: ${response.status}`);
      }

      const responseData = await response.json();
      setLoading(false);
      setUploadSuccess(true);

      // Update wizard state with file data
      onUpdateState({
        fileId: responseData.fileId,
        filePath: responseData.filePath, // Store the file path for job creation
        fileName: fileToUpload.name,
        uploadedFile: fileToUpload,
        rowCount: responseData.rowCount,
        columnCount: responseData.columnCount,
        columns: responseData.columns || []
      });

      console.log('Research file uploaded successfully:', responseData);
    } catch (error) {
      setLoading(false);
      console.error('Upload error:', error);
      setError(error.message || 'An error occurred during upload');
    }
  };

  // Drag handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFile = e.dataTransfer.files[0];
      validateAndUpload(droppedFile);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* 50/50 Layout: Name field + template on left, drag & drop on right */}
      <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
        {/* Left side: Job Name and Template section */}
        <Box sx={{
          flex: 1,
          backgroundColor: '#161c3e',
          borderRadius: 2,
          p: 3
        }}>
          {/* Job Name Input Field */}
          <Box sx={{ mb: 3 }}>
            <TextField
              label="Job Name"
              value={wizardState.jobName || ''}
              onChange={(e) => onUpdateState({ jobName: e.target.value })}
              placeholder="Give this research job a descriptive name"
              required
              fullWidth
              variant="outlined"
              helperText="The results file you download will have this name"
              InputLabelProps={{
                sx: {
                  '&.Mui-required .MuiInputLabel-asterisk': {
                    display: 'none'
                  }
                }
              }}
            />
          </Box>

          {/* Template Dropdown */}
          <Box sx={{ width: '100%' }}>
            <Typography variant="h6" gutterBottom>
              <BookmarkIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Use a Template
            </Typography>

            <FormControl sx={{ width: '100%', mb: 2 }}>
              <InputLabel id="template-select-label">Select Template</InputLabel>
              <Select
                labelId="template-select-label"
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                label="Select Template"
                disabled={loadingTemplates}
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {templates.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name} {t.is_favorite && '⭐'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              color="secondary"
              onClick={handleApplyTemplate}
              disabled={!selectedTemplateId || !wizardState.fileId || applyingTemplate || loading}
              sx={{
                textTransform: 'none',
                borderRadius: '4px',
                fontWeight: 'medium',
                px: 3,
                width: '100%'
              }}
            >
              {applyingTemplate ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Apply Template'}
            </Button>

            {!wizardState.fileId && selectedTemplateId && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Upload a file first to apply a template
              </Typography>
            )}
          </Box>
        </Box>

        {/* Right side: Drag & Drop area */}
        <Box sx={{ flex: 1 }}>
          <Box
            component={Paper}
            variant="outlined"
            sx={{
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#161c3e',
              border: `2px dashed ${dragActive ? 'rgba(67, 97, 238, 0.6)' : 'rgba(255, 255, 255, 0.1)'}`,
              borderRadius: 2,
              transition: 'all 0.2s ease',
              minHeight: '300px',
              '&:hover': {
                borderColor: 'rgba(67, 97, 238, 0.4)',
                backgroundColor: '#1a2046'
              }
            }}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              accept=".csv,.xlsx,.xls"
              style={{ display: 'none' }}
              id="research-file-input"
              type="file"
              onChange={handleFileChange}
            />

            {!uploadSuccess ? (
              <>
                <CloudUploadIcon sx={{ fontSize: 64, color: dragActive ? 'primary.light' : 'primary.main', mb: 2, transition: 'color 0.2s ease' }} />
                <Typography variant="h6" gutterBottom>
                  {file ? file.name : 'Drag & drop CSV file here'}
                </Typography>
                <label htmlFor="research-file-input">
                  <Button
                    variant="outlined"
                    component="span"
                    sx={{
                      borderColor: 'rgba(255,255,255,0.3)',
                      color: 'text.secondary',
                      textTransform: 'none',
                      borderRadius: '4px'
                    }}
                  >
                    Browse Files
                  </Button>
                </label>
              </>
            ) : (
              <>
                <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Upload Successful
                </Typography>
                {wizardState.rowCount && wizardState.columnCount && (
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {wizardState.rowCount} rows and {wizardState.columnCount} columns detected
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
